import { useTranslation } from 'react-i18next';

import { IncubatorMapSlot } from '@/app/services/fertWork';
import { getEmbryoCategoryColorClass } from '@/utils/categoryColors';

export type SlotContentProps = {
  slot: IncubatorMapSlot;
  isDragging: boolean;
  slotHeightClass?: string;
  isCommercial?: boolean;
  secondSlot?: IncubatorMapSlot;
  fourMating?: boolean;
};

export const SlotContent = ({
  slot,
  isDragging,
  slotHeightClass = 'min-h-[132px] sm:min-h-[148px]',
  isCommercial = false,
  secondSlot,
  fourMating = false,
}: SlotContentProps) => {
  const { t } = useTranslation();
  const colorClass = getEmbryoCategoryColorClass({
    isDay5: slot.isDay5,
    planCategory: slot.planCategory,
  });
  const topMating = slot.label?.top;
  const bottomMating = slot.label?.bottom;
  const isMassProduction = !!(
    slot.isMassProduction || slot.label?.isMassProduction
  );
  const isNonEU = slot.planCategory === 'ExportNonEU';
  const exportTag = slot.planCategory?.startsWith('Export')
    ? slot.planCategory.replace('Export', '')
    : null;
  const slotSpecies =
    slot.label?.top?.speciesSubfamily ??
    slot.label?.bottom?.speciesSubfamily ??
    null;
  const speciesTextClass =
    slotSpecies === 'Caprine'
      ? 'text-green-700'
      : slotSpecies === 'Ovine'
        ? 'text-orange-700'
        : null;
  const textClass =
    speciesTextClass ?? (isNonEU ? 'text-red-600' : 'text-gray-700');

  if (fourMating) {
    const secondTop = secondSlot?.label?.top;
    const secondBottom = secondSlot?.label?.bottom;
    return (
      <div
        className={`relative flex ${slotHeightClass} flex-col rounded-lg border p-1.5 pl-4 transition-all ${colorClass} ${
          isDragging ? 'ring-2 ring-blue-500 ring-offset-2' : ''
        }`}
      >
        {(slot.isDay5 || exportTag) && (
          <div className="absolute top-1 right-1 flex flex-col items-end gap-0.5">
            {slot.isDay5 && (
              <span className="rounded bg-purple-100 px-1 py-0.5 text-[8px] font-bold text-purple-700">
                {t('fertWork.day5Badge', 'DAY 5')}
              </span>
            )}
            {exportTag && (
              <span
                className={`rounded px-1 py-0.5 text-[8px] font-bold ${isNonEU ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}
              >
                {exportTag}
              </span>
            )}
          </div>
        )}
        <div className="flex h-5 items-center">
          <div className="text-xs font-semibold text-gray-800">
            {slot.slotPosition}
          </div>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-0.5">
          <div
            className={`overflow-hidden text-[10px] leading-tight ${textClass}`}
          >
            <div>
              <span className="font-semibold text-gray-600">D:</span>{' '}
              {topMating?.donorName || '-'}
            </div>
            <div>
              <span className="font-semibold text-gray-600">S:</span>{' '}
              {topMating?.sireName || '-'}
            </div>
          </div>
          <div
            className={`overflow-hidden text-[10px] leading-tight ${textClass}`}
          >
            <div>
              <span className="font-semibold text-gray-600">D:</span>{' '}
              {bottomMating?.donorName || '-'}
            </div>
            <div>
              <span className="font-semibold text-gray-600">S:</span>{' '}
              {bottomMating?.sireName || '-'}
            </div>
          </div>
          <div
            className={`overflow-hidden text-[10px] leading-tight ${textClass}`}
          >
            <div>
              <span className="font-semibold text-gray-600">D:</span>{' '}
              {secondTop?.donorName || '-'}
            </div>
            <div>
              <span className="font-semibold text-gray-600">S:</span>{' '}
              {secondTop?.sireName || '-'}
            </div>
          </div>
          <div
            className={`overflow-hidden text-[10px] leading-tight ${textClass}`}
          >
            <div>
              <span className="font-semibold text-gray-600">D:</span>{' '}
              {secondBottom?.donorName || '-'}
            </div>
            <div>
              <span className="font-semibold text-gray-600">S:</span>{' '}
              {secondBottom?.sireName || '-'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative flex ${slotHeightClass} flex-col justify-start rounded-lg ${isMassProduction ? 'border-2 border-gray-900' : 'border'} p-2 pl-5 transition-all ${colorClass} ${
        isDragging ? 'ring-2 ring-blue-500 ring-offset-2' : ''
      }`}
    >
      {(slot.isDay5 || exportTag) && (
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          {slot.isDay5 && (
            <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700 sm:text-xs">
              {t('fertWork.day5Badge', 'DAY 5')}
            </span>
          )}
          {exportTag && (
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-bold sm:text-xs ${isNonEU ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}
            >
              {exportTag}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-1 flex-col">
        <div className="flex h-6 items-center pr-1 sm:h-7">
          <div className="text-sm font-semibold text-gray-800 sm:text-base">
            {slot.slotPosition}
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col justify-center gap-0.5 pr-1">
          <div
            className={`text-xs leading-snug break-words whitespace-normal sm:text-sm ${textClass}`}
          >
            {!isMassProduction && (
              <span className="font-semibold text-gray-600">D:</span>
            )}{' '}
            {isMassProduction && topMating?.poolNumber != null
              ? `Pool-${topMating.poolNumber}`
              : topMating?.donorName || slot.donorName || '-'}
            {isCommercial && topMating?.opuSiteName && (
              <span className="ml-1 text-[10px] text-gray-500">
                [{topMating.opuSiteName}]
              </span>
            )}
            {isCommercial && topMating?.priority != null && (
              <span className="ml-1 rounded bg-amber-50 px-1 py-[1px] text-[9px] font-semibold text-amber-700 ring-1 ring-amber-200">
                P{topMating.priority}
              </span>
            )}
          </div>
          {isCommercial && (topMating?.donorBreed || topMating?.sireBreed) && (
            <div className="text-[10px] leading-tight text-gray-500">
              {topMating?.donorBreed || '—'} × {topMating?.sireBreed || '—'}
            </div>
          )}
          <div
            className={`text-xs leading-snug break-words whitespace-normal sm:text-sm ${textClass}`}
          >
            <span className="font-semibold text-gray-600">S:</span>{' '}
            {topMating?.sireName || slot.sireName || '-'}
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col justify-center gap-0.5 border-t border-gray-300 pt-1">
          {bottomMating ? (
            <>
              <div
                className={`text-xs leading-snug break-words whitespace-normal sm:text-sm ${textClass}`}
              >
                {!isMassProduction && (
                  <span className="font-semibold text-gray-600">D:</span>
                )}{' '}
                {isMassProduction && bottomMating.poolNumber != null
                  ? `Pool-${bottomMating.poolNumber}`
                  : bottomMating.donorName}
                {isCommercial && bottomMating.opuSiteName && (
                  <span className="ml-1 text-[10px] text-gray-500">
                    [{bottomMating.opuSiteName}]
                  </span>
                )}
                {isCommercial && bottomMating.priority != null && (
                  <span className="ml-1 rounded bg-amber-50 px-1 py-[1px] text-[9px] font-semibold text-amber-700 ring-1 ring-amber-200">
                    P{bottomMating.priority}
                  </span>
                )}
              </div>
              {isCommercial &&
                (bottomMating.donorBreed || bottomMating.sireBreed) && (
                  <div className="text-[10px] leading-tight text-gray-500">
                    {bottomMating.donorBreed || '—'} ×{' '}
                    {bottomMating.sireBreed || '—'}
                  </div>
                )}
              <div
                className={`text-xs leading-snug break-words whitespace-normal sm:text-sm ${textClass}`}
              >
                <span className="font-semibold text-gray-600">S:</span>{' '}
                {bottomMating.sireName || '-'}
              </div>
            </>
          ) : (
            <div className="text-center text-xs font-semibold tracking-wide text-gray-500 sm:text-sm">
              EMPTY
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
